import React, { useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { createPortal } from "react-dom";
import { db, auth } from "../firebaseConfig";
import {
  collection,
  getDocs,
  query,
  where,
  writeBatch,
  doc,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";

const INOSEL_ID = "27517";

// Koleksiyonlar
const COL_ZONE_URETIM = "hazirbeton_zone_uretim";
const COL_MIKSER_SEVKIYAT = "hazirbeton_mikser_sevkiyat";
const COL_POMPA_HAKEDIS = "hazirbeton_pompa_hakedis";

// Excel kolon indexleri (0-based) – SENİN ŞABLONUNA GÖRE
// A: TESİS (0)
// D: İrsaliye Tarihi (3)
// G: İrsaliye No (6)
// H: Müşteri Adı (7)
// J: Şantiye Adı (9)
// K: T.Mikser Plaka (10)
// L: Teslim Şekli (11)
// M: Satış M3 (12)
// T: Pompa Plaka (19)
// V: Voto KM (21)  ✅
// Y: Mikser Firma ID (24)  ✅ (22 idi, 2 sütun sağa kaydı)
// Z: Pompa Firma ID (25)   ✅ (23 idi, 2 sütun sağa kaydı)

const norm = (s) =>
  (s ?? "")
    .toString()
    .trim()
    .toLocaleUpperCase("tr-TR")
    .replace(/\s+/g, " ");

const normPlate = (s) =>
  (s ?? "")
    .toString()
    .trim()
    .toLocaleUpperCase("tr-TR")
    .replace(/\s+/g, "");

const uniqSorted = (arr) => [...new Set(arr)].sort((a, b) => (a > b ? 1 : -1));

function parseDateToYMD(val) {
  if (!val && val !== 0) return "";
  if (typeof val === "number") {
    const d = XLSX.SSF.parse_date_code(val);
    if (!d?.y) return "";
    return `${d.y}-${String(d.m).padStart(2, "0")}-${String(d.d).padStart(2, "0")}`;
  }
  if (val instanceof Date) {
    const y = val.getFullYear();
    const m = String(val.getMonth() + 1).padStart(2, "0");
    const day = String(val.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }
  const str = val.toString().trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
  const m = str.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  return "";
}

function ymdToLocalMidnightTimestamp(ymd) {
  if (!ymd) return null;
  const [y, m, d] = ymd.split("-").map(Number);
  return Timestamp.fromDate(new Date(y, m - 1, d, 0, 0, 0, 0));
}

/** ======= Filter Dropdown (mevcut tasarım bozulmasın) ======= */
function FilterDropdown({ open, anchorRect, values, selected, onToggle, onClose }) {
  const boxRef = useRef(null);

  useEffect(() => {
    const onDown = (e) => {
      if (!open) return;
      if (boxRef.current && !boxRef.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open, onClose]);

  if (!open || !anchorRect) return null;

  const style = {
    position: "absolute",
    top: anchorRect.bottom + window.scrollY + 4,
    left: anchorRect.left + window.scrollX,
    background: "#fff",
    border: "1px solid #cfcfcf",
    borderRadius: 6,
    padding: 8,
    zIndex: 9999,
    boxShadow: "0 6px 18px rgba(0,0,0,0.18)",
    maxHeight: 260,
    overflowY: "auto",
    overflowX: "hidden",
    minWidth: 260,
    maxWidth: 420,
  };

  return createPortal(
    <div ref={boxRef} style={style}>
      <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 13 }}>Filtre</div>

      {values.length === 0 ? (
        <div style={{ fontSize: 12, color: "#666" }}>Değer yok</div>
      ) : (
        values.map((v) => (
          <label
            key={v}
            style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 2px", cursor: "pointer" }}
          >
            <input type="checkbox" checked={selected.includes(v)} onChange={() => onToggle(v)} />
            <span style={{ fontSize: 13, wordBreak: "break-word" }}>{v || "(Boş)"}</span>
          </label>
        ))
      )}

      <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
        <button
          type="button"
          onClick={onClose}
          style={{
            padding: "6px 10px",
            border: "1px solid #cfcfcf",
            borderRadius: 6,
            background: "#f7f7f7",
            cursor: "pointer",
          }}
        >
          Kapat
        </button>
      </div>
    </div>,
    document.body
  );
}

export default function ZoneYukle() {
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({});
  const [fileName, setFileName] = useState("");
  const [saving, setSaving] = useState(false);

  const [openFilterKey, setOpenFilterKey] = useState(null);
  const [anchorRect, setAnchorRect] = useState(null);

  const fileInputRef = useRef(null);
  const spaceShortcutRef = useRef(false);

  const columns = useMemo(
    () => [
      { key: "tarihStr", label: "Tarih" },
      { key: "irsaliyeNo", label: "İrsaliye No" },
      { key: "mikserPlaka", label: "Mikser" },
      { key: "pompaPlaka", label: "Pompa" },
      { key: "mikserFirmaId", label: "Mikser ID" },
      { key: "pompaFirmaId", label: "Pompa ID" },
      { key: "musteriAdi", label: "Müşteri" },
      { key: "santiyeAdi", label: "Şantiye" },
      { key: "satisM3", label: "Satış M3" },
      { key: "teslimSekli", label: "Teslim Şekli" },
      { key: "votoKm", label: "Voto KM" },
    ],
    []
  );

  const filteredRows = useMemo(() => {
    return rows.filter((r) =>
      Object.entries(filters).every(([k, selectedVals]) => {
        if (!selectedVals || selectedVals.length === 0) return true;
        return selectedVals.includes(String(r[k] ?? ""));
      })
    );
  }, [rows, filters]);

  const toggleFilterValue = (key, value) => {
    setFilters((prev) => {
      const cur = prev[key] || [];
      const next = cur.includes(value) ? cur.filter((x) => x !== value) : [...cur, value];
      return { ...prev, [key]: next };
    });
  };

  const filterValues = (key) => uniqSorted(rows.map((r) => String(r[key] ?? "")));

  const totalSatisM3 = useMemo(
    () => filteredRows.reduce((t, r) => t + (Number(r.satisM3) || 0), 0),
    [filteredRows]
  );

  // Duplicate kontrol (tarihStr üzerinden hızlı)
  const fetchExistingIrsaliyeler = async (tarihStrList) => {
    const existing = new Set();
    const dates = uniqSorted(tarihStrList.filter(Boolean));
    for (let i = 0; i < dates.length; i += 10) {
      const chunk = dates.slice(i, i + 10);
      const qy = query(collection(db, COL_ZONE_URETIM), where("tarihStr", "in", chunk));
      const snap = await getDocs(qy);
      snap.forEach((d) => {
        const data = d.data();
        if (data?.irsaliyeNo) existing.add(String(data.irsaliyeNo));
      });
    }
    return existing;
  };

  const handleFileChange = async (file) => {
    if (!file) return;
    setFileName(file.name);

    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: "array" });
    const ws = wb.Sheets[wb.SheetNames[0]];

    let data = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });

    // header satırı bul
    let startIdx = 0;
    for (let i = 0; i < Math.min(data.length, 15); i++) {
      const g = norm(data[i]?.[6]);
      if (g.includes("İRSALİYE") || g.includes("IRSALIYE")) {
        startIdx = i + 1;
        break;
      }
    }
    data = data.slice(startIdx);

    const tarihler = [];
    const rawRows = [];

    for (const r of data) {
      if (!r || r.length < 7) continue;

      const irsaliyeNo = String(r[6] ?? "").trim();
      if (!irsaliyeNo) continue;
      const gNorm = norm(irsaliyeNo);
      if (gNorm.includes("İRSALİYE") || gNorm.includes("IRSALIYE")) continue;

      const tarihStr = parseDateToYMD(r[3]);
      const tarih = ymdToLocalMidnightTimestamp(tarihStr);

      const tesis = String(r[0] ?? "").trim(); // ✅ A sütunu

      const mikserFirmaId = String(r[24] ?? "").trim() || "0"; // ✅ Y
      const pompaFirmaId = String(r[25] ?? "").trim() || "0";  // ✅ Z

      const isInosel = mikserFirmaId === INOSEL_ID || pompaFirmaId === INOSEL_ID;

      const votoRaw = r[21];
      const votoParsed = Number(String(votoRaw ?? "").replace(",", ".").trim());
      const votoKm = Number.isFinite(votoParsed) ? votoParsed : null;

      const rowObj = {
        _isInosel: isInosel,
        _checked: isInosel,
        _duplicate: false,

        tarihStr,
        tarih, // ✅ Timestamp (local midnight)
        irsaliyeNo,
        mikserPlaka: normPlate(r[10]),
        pompaPlaka: normPlate(r[19]),
        mikserFirmaId,
        pompaFirmaId,
        musteriAdi: String(r[7] ?? "").trim(),
        santiyeAdi: String(r[9] ?? "").trim(),
        satisM3: Number(r[12] ?? 0) || 0,
        teslimSekli: String(r[11] ?? "").trim(),
        votoKm,
        tesis, // ✅ Firestore'a yazılacak
      };

      rawRows.push(rowObj);
      if (tarihStr) tarihler.push(tarihStr);
    }

    let existingSet = new Set();
    try {
      existingSet = await fetchExistingIrsaliyeler(tarihler);
    } catch (e) {
      console.error("Existing irsaliye çekilemedi:", e);
    }

    const finalRows = rawRows.map((x) => {
      const dup = existingSet.has(String(x.irsaliyeNo));
      return { ...x, _duplicate: dup, _checked: x._isInosel && !dup };
    });

    setRows(finalRows);
    setFilters({});
    setOpenFilterKey(null);
    setAnchorRect(null);
  };

  const handleSave = async () => {
    if (!rows.length || saving) return;
    setSaving(true);
    const email = auth.currentUser?.email || "";
    const batch = writeBatch(db);

    let willWriteAll = 0;
    let willWriteMikser = 0;
    let willWritePompa = 0;

    for (const r of rows) {
      if (r._duplicate) continue;

      // 1) tüm satırlar
      batch.set(doc(collection(db, COL_ZONE_URETIM)), {
        tarih: r.tarih ?? null,       // ✅ Timestamp
        tarihStr: r.tarihStr || "",   // ✅ String (uyumluluk)
        irsaliyeNo: r.irsaliyeNo || "",
        mikserPlaka: r.mikserPlaka || "",
        pompaPlaka: r.pompaPlaka || "",
        mikserFirmaId: r.mikserFirmaId || "0",
        pompaFirmaId: r.pompaFirmaId || "0",
        musteriAdi: r.musteriAdi || "",
        santiyeAdi: r.santiyeAdi || "",
        satisM3: Number(r.satisM3) || 0,
        teslimSekli: r.teslimSekli || "",
        votoKm: r.votoKm ?? null,
        tesis: r.tesis || "",         // ✅ EKLENDİ
        kaynak: "zone",
        createdAt: serverTimestamp(),
        createdBy: email,
        deleted: false,
      });
      willWriteAll++;

      // 2) sadece bizim iş (tickli)
      if (r._checked && r._isInosel) {
        if (r.mikserFirmaId === INOSEL_ID) {
          batch.set(doc(collection(db, COL_MIKSER_SEVKIYAT)), {
            tarih: r.tarih ?? null,
            tarihStr: r.tarihStr || "",
            irsaliyeNo: r.irsaliyeNo || "",
            mikserPlaka: r.mikserPlaka || "",
            pompaPlaka: r.pompaPlaka || "",
            mikserFirmaId: r.mikserFirmaId || "0",
            pompaFirmaId: r.pompaFirmaId || "0",
            musteriAdi: r.musteriAdi || "",
            santiyeAdi: r.santiyeAdi || "",
            m3: Number(r.satisM3) || 0,
            teslimSekli: r.teslimSekli || "",
            votoKm: r.votoKm ?? null,
            tesis: r.tesis || "",       // ✅ EKLENDİ
            kaynak: "zone",
            soforAdi: "",
            cikisKm: null,
            donusKm: null,
            cikisSaat: "",
            donusSaat: "",
            tamamlandi: false,
            deleted: false,

            createdAt: serverTimestamp(),
            createdBy: email,
          });
          willWriteMikser++;
        }

        if (r.pompaFirmaId === INOSEL_ID) {
          batch.set(doc(collection(db, COL_POMPA_HAKEDIS)), {
            tarih: r.tarih ?? null,
            tarihStr: r.tarihStr || "",
            santiyeAdi: r.santiyeAdi || "",
            musteriAdi: r.musteriAdi || "",
            pompaPlaka: r.pompaPlaka || "",
            pompaFirmaId: r.pompaFirmaId || "0",
            mikserPlaka: r.mikserPlaka || "",
            irsaliyeNo: r.irsaliyeNo || "",
            m3: Number(r.satisM3) || 0,
            teslimSekli: r.teslimSekli || "",
            votoKm: r.votoKm ?? null,
            tesis: r.tesis || "",       // ✅ EKLENDİ
            kaynak: "zone",
            operatorAdi: "",
            yagciAdi: "",
            baslangicSaat: "",
            bitisSaat: "",
            tamamlandi: false,
            deleted: false,

            createdAt: serverTimestamp(),
            createdBy: email,
          });
          willWritePompa++;
        }
      }
    }

    try {
      await batch.commit();
      alert(
        `Kayıt tamamlandı.\n` +
          `• Tüm üretim satırı: ${willWriteAll}\n` +
          `• Bizim mikser işi (çarşaf): ${willWriteMikser}\n` +
          `• Bizim pompa işi: ${willWritePompa}\n` +
          `• Duplicate olanlar kaydedilmedi.`
      );
      setRows([]);
      setFilters({});
      setOpenFilterKey(null);
      setAnchorRect(null);
      setFileName("");
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (e) {
      console.error("Kayıt sırasında hata:", e);
      alert("Kayıt sırasında hata oluştu.");
    } finally {
      setSaving(false);
    }
  };

  const onClickFilter = (key, e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    if (openFilterKey === key) {
      setOpenFilterKey(null);
      setAnchorRect(null);
      return;
    }
    setOpenFilterKey(key);
    setAnchorRect(rect);
  };

  const closeFilter = () => {
    setOpenFilterKey(null);
    setAnchorRect(null);
  };

  useEffect(() => {
    const isTypingTarget = (target) => {
      const tag = String(target?.tagName || "").toLowerCase();
      return tag === "input" || tag === "textarea" || tag === "select" || target?.isContentEditable;
    };
    const onKeyDown = (e) => {
      if (e.code === "Space" && !isTypingTarget(e.target)) {
        spaceShortcutRef.current = true;
        return;
      }
      if (e.key !== "Enter" || isTypingTarget(e.target)) return;
      e.preventDefault();
      if (spaceShortcutRef.current) handleSave();
      else fileInputRef.current?.click();
    };
    const onKeyUp = (e) => {
      if (e.code === "Space") spaceShortcutRef.current = false;
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
    // eslint-disable-next-line
  }, [rows, saving]);

  return (
    <div style={{ padding: 12 }}>
      <h3 style={{ marginTop: 0 }}>Zone Yükle</h3>

      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xls,.xlsx"
          onChange={(e) => handleFileChange(e.target.files?.[0])}
          title="Enter"
        />
        {fileName ? <div style={{ fontSize: 12, color: "#444" }}>{fileName}</div> : null}
        <button
          type="button"
          onClick={handleSave}
          disabled={!rows.length || saving}
          style={{ marginLeft: "auto", ...hbButtonStyle("primary", !rows.length || saving) }}
          title="Space + Enter"
        >
          {saving ? "Kaydediliyor..." : "Kaydet"}
        </button>
      </div>

      {rows.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr>
                <th style={thStyle}></th>
                {columns.map((c) => (
                  <th key={c.key} style={{ ...thStyle, position: "relative" }}>
                    {c.label}{" "}
                    <span
                      style={{ cursor: "pointer", userSelect: "none" }}
                      onClick={(e) => onClickFilter(c.key, e)}
                      title="Filtre"
                    >
                      ▼
                    </span>
                  </th>
                ))}
              </tr>

              <tr style={{ background: "#fff9c4", fontWeight: 700 }}>
                <td style={tdStyle}></td>
                <td style={tdStyle} colSpan={columns.length - 3}>
                  TOPLAM ({filteredRows.length})
                </td>
                <td style={tdStyle}>{totalSatisM3}</td>
                <td style={tdStyle}></td>
                <td style={tdStyle}></td>
              </tr>
            </thead>

            <tbody>
              {filteredRows.map((r, idx) => {
                const disabled = !r._isInosel || r._duplicate;
                return (
                  <tr
                    key={`${r.irsaliyeNo}-${idx}`}
                    style={
                      r._duplicate
                        ? { background: "#ffecec" }
                        : r._isInosel
                        ? {}
                        : { background: "#fafafa" }
                    }
                    title={
                      r._duplicate
                        ? "Bu irsaliye daha önce yüklenmiş (duplicate). Kaydedilmez."
                        : ""
                    }
                  >
                    <td style={tdStyle}>
                      <input
                        type="checkbox"
                        checked={!!r._checked}
                        disabled={disabled}
                        onChange={() =>
                          setRows((prev) =>
                            prev.map((x) => (x === r ? { ...x, _checked: !x._checked } : x))
                          )
                        }
                      />
                    </td>

                    {columns.map((c) => (
                      <td key={c.key} style={tdStyle}>
                        {c.key === "satisM3" ? Number(r[c.key] || 0) : String(r[c.key] ?? "")}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>

          <FilterDropdown
            open={!!openFilterKey}
            anchorRect={anchorRect}
            values={openFilterKey ? filterValues(openFilterKey) : []}
            selected={openFilterKey ? filters[openFilterKey] || [] : []}
            onToggle={(v) => openFilterKey && toggleFilterValue(openFilterKey, v)}
            onClose={closeFilter}
          />
        </div>
      )}
    </div>
  );
}

const thStyle = {
  border: "1px solid #cfcfcf",
  padding: "6px 6px",
  background: "#f5f5f5",
  textAlign: "left",
  whiteSpace: "nowrap",
};

const tdStyle = {
  border: "1px solid #e0e0e0",
  padding: "6px 6px",
  verticalAlign: "top",
};

const hbButtonStyle = (variant = "secondary", disabled = false) => ({
  border: "1px solid " + (variant === "primary" ? "#1d4ed8" : "#cbd5e1"),
  borderRadius: 7,
  padding: "7px 14px",
  fontWeight: 700,
  color: disabled ? "#94a3b8" : variant === "primary" ? "#fff" : "#0f172a",
  background: disabled
    ? "#f1f5f9"
    : variant === "primary"
      ? "linear-gradient(180deg, #3b82f6, #2563eb)"
      : "linear-gradient(180deg, #fff, #f8fafc)",
  boxShadow: disabled ? "none" : "0 1px 2px rgba(15,23,42,0.12)",
  cursor: disabled ? "not-allowed" : "pointer",
});
